import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { DeptMapping } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { GroupPicker } from '@/components/GroupPicker'
import { SearchableGroupPicker } from '@/components/SearchableGroupPicker'
import { PartnerPicker } from '@/components/PartnerPicker'
import { SearchBox } from '@/components/SearchBox'
import { Field, inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'
import { useListNavigation } from '@/lib/useListNavigation'

interface DeptForm {
  id?: number
  group: number | null
  group_label: string
  dept_code: string
  partner: number | null
  partner_label: string
}

const blankForm: DeptForm = {
  group: null, group_label: '',
  dept_code: '',
  partner: null, partner_label: '',
}

export function DeptMappingsTab() {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [groupId, setGroupId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState<DeptForm | null>(null)

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['dept-mappings', groupId, search],
    queryFn: () => {
      const params = new URLSearchParams()
      if (groupId) params.set('group_id', String(groupId))
      if (search) params.set('q', search)
      return api<DeptMapping[]>(`/api/dept-mappings?${params}`)
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => api(`/api/dept-mappings/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dept-mappings'] })
      push('삭제됨', 'success')
    },
    onError: (e) => push(`삭제 실패: ${e.message}`, 'error', 5000),
  })

  const save = useMutation({
    mutationFn: (f: DeptForm) => {
      const body = {
        partner: f.partner,             // group 은 서버가 partner 에서 파생
        dept_code: f.dept_code.trim(),
      }
      return f.id
        ? api<DeptMapping>(`/api/dept-mappings/${f.id}`, { method: 'PATCH', body })
        : api<DeptMapping>('/api/dept-mappings', { method: 'POST', body })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dept-mappings'] })
      setEdit(null); push('저장됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 6000),
  })

  function openAdd() {
    setEdit({ ...blankForm })
  }

  function openEdit(r: DeptMapping) {
    setEdit({
      id: r.id,
      group: r.group_id, group_label: r.group_name ?? '',
      dept_code: r.dept_code,
      partner: r.partner,
      partner_label: r.partner_name ? `${r.partner_code} ${r.partner_name}` : '',
    })
  }

  const { tbodyRef, onKeyDown: onListKeyDown } = useListNavigation<DeptMapping>({
    items: rows,
    disabled: !!edit,
    onEnter: (r) => openEdit(r),
    onDelete: (r) => {
      void confirm(`부서 '${r.dept_code}' 매핑 삭제?`, { danger: true }).then((ok) => ok && remove.mutate(r.id))
    },
  })

  const canSubmit = edit
    ? !!edit.group && !!edit.dept_code.trim() && !!edit.partner
    : false

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-slate-50/95 backdrop-blur border-b border-slate-200/60 mb-3">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm">
        <SearchableGroupPicker
          value={groupId}
          onChange={(g) => setGroupId(g?.id ?? null)}
          placeholder="상위 거래처 필터"
          className="w-64"
        />
        <SearchBox value={search} onChange={setSearch}
                   placeholder="부서코드/거래처 검색" className="flex-1 min-w-[200px]" />
        <span className="text-xs text-slate-400 tabular-nums">{rows.length}건</span>
        <button onClick={openAdd} data-role="add-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5"/> 매핑 추가
        </button>
      </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium w-56">상위 거래처</th>
              <th className="px-3 py-2.5 text-left font-medium">발주서 부서</th>
              <th className="px-3 py-2.5 text-center font-medium w-8"></th>
              <th className="px-3 py-2.5 text-left font-medium w-72">거래처</th>
              <th className="px-3 py-2.5 text-right font-medium w-20"></th>
            </tr>
          </thead>
          <tbody ref={tbodyRef} onKeyDown={onListKeyDown}>
            {rows.map((r, idx) => (
              <tr key={r.id} data-idx={idx} tabIndex={idx === 0 ? 0 : -1}
                  className="border-t border-slate-100 hover:bg-slate-50 focus:bg-blue-50 focus:outline-none">
                <td className="px-3 py-1.5 text-xs font-medium text-slate-800">{r.group_name ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-700">{r.dept_code}</td>
                <td className="px-3 py-1.5 text-center text-slate-300"><ArrowRight className="w-3.5 h-3.5 inline"/></td>
                <td className="px-3 py-1.5 text-slate-800">
                  <span className="font-mono text-xs text-slate-400 mr-1.5">{r.partner_code}</span>{r.partner_name}
                </td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} title="편집"
                          className="text-blue-600 hover:text-blue-800 mr-2">
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                  <button onClick={() => { void confirm(`부서 '${r.dept_code}' 매핑 삭제?`, { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                          className="text-rose-500 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && !isFetching && (
              <tr>
                <td colSpan={5} className={cn('text-center py-10 text-sm text-slate-400')}>
                  부서 매핑이 없습니다. 우측 상단 "매핑 추가"로 시작하세요.
                  <br/>
                  <span className="text-xs">발주서의 부서코드를 상위 거래처의 자식 매출처로 연결합니다.</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)}
             title={edit?.id ? '부서 매핑 수정' : '부서 매핑 추가'} size="lg">
        {edit && (
          <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) save.mutate(edit) }}
                className="space-y-3">
            <Field label="상위 거래처 *" full>
              <GroupPicker
                value={edit.group}
                onChange={(g) => setEdit({
                  ...edit, group: g.id, group_label: `${g.name} · ${g.member_count}개`,
                  // 그룹 바뀌면 거래처 초기화 (다른 그룹의 자식 방지)
                  partner: null, partner_label: '',
                })}
                placeholder={edit.group_label || '상위 거래처 선택'}
              />
            </Field>

            <Field label="발주서 부서 *" full>
              <input value={edit.dept_code}
                     onChange={(e) => setEdit({ ...edit, dept_code: e.target.value })}
                     placeholder="예: 1904-Kitchen - Banquets"
                     className={inputCls + ' font-mono'}/>
              <div className="text-[11px] text-slate-400 mt-1">
                발주서에 찍힌 부서명/코드를 그대로 입력하세요. 발주서 변환 시 이 값으로 자식 거래처를 찾습니다.
              </div>
            </Field>

            <Field label="거래처 (자식 매출처) *" full>
              <PartnerPicker
                value={edit.partner}
                groupId={edit.group}
                onChange={(p) => setEdit({
                  ...edit, partner: p.id, partner_label: `${p.code} ${p.name}`,
                })}
                placeholder={
                  edit.group
                    ? (edit.partner_label || '자식 거래처 선택')
                    : '먼저 상위 거래처를 선택하세요'
                }
              />
              {!edit.group && (
                <div className="text-[11px] text-amber-600 mt-1">상위 거래처를 먼저 선택해야 자식 거래처가 필터링됩니다.</div>
              )}
            </Field>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)}
                      className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit"
                      disabled={!canSubmit || save.isPending}
                      className={cn(
                        'px-4 py-2 text-sm font-semibold rounded-lg',
                        (!canSubmit || save.isPending)
                          ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white',
                      )}>
                {save.isPending ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
